/**
 * Location Accuracy Toggle Component
 *
 * Allows users to choose their preferred location accuracy mode.
 * Higher accuracy is better for room creation and POI reporting,
 * while quick mode is faster for general discovery.
 */

import { useCallback } from 'react'
import {
  type LocationAccuracyMode,
  ACCURACY_MODES,
  setStoredAccuracyMode,
} from './locationAccuracyUtils'


export interface LocationAccuracyToggleProps {
  value: LocationAccuracyMode
  onChange: (mode: LocationAccuracyMode) => void
  compact?: boolean
  className?: string
}

export function LocationAccuracyToggle({
  value,
  onChange,
  compact = false,
  className = '',
}: LocationAccuracyToggleProps) {
  const handleChange = useCallback(
    (mode: LocationAccuracyMode) => {
      onChange(mode)
      setStoredAccuracyMode(mode)
    },
    [onChange]
  )

  const modes: LocationAccuracyMode[] = ['precise', 'balanced', 'quick']

  if (compact) {
    return (
      <div className={`flex bg-black/40 rounded-lg p-1 space-x-1 border border-white/10 ${className}`}>
        <button
          onClick={() => handleChange('precise')}
          className={`flex flex-col items-center justify-center w-10 h-10 rounded-md transition-all ${value === 'precise'
            ? 'bg-green-500/20 border border-green-500/50 text-green-400 shadow-[0_0_10px_rgba(34,197,94,0.3)]'
            : 'text-white/30 hover:bg-white/5 hover:text-white/60'
            }`}
          title="Precise (GPS)"
        >
          <span className="material-icons-round text-sm">my_location</span>
          <span className="text-[6px] font-bold uppercase mt-0.5 tracking-wider">GPS</span>
        </button>
        <button
          onClick={() => handleChange('balanced')}
          className={`flex flex-col items-center justify-center w-10 h-10 rounded-md transition-all ${value === 'balanced'
            ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.3)]'
            : 'text-white/30 hover:bg-white/5 hover:text-white/60'
            }`}
          title="Balanced (WiFi/Cell)"
        >
          <span className="material-icons-round text-sm">navigation</span>
          <span className="text-[6px] font-bold uppercase mt-0.5 tracking-wider">NET</span>
        </button>
        <button
          onClick={() => handleChange('quick')}
          className={`flex flex-col items-center justify-center w-10 h-10 rounded-md transition-all ${value === 'quick'
            ? 'bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 shadow-[0_0_10px_rgba(234,179,8,0.3)]'
            : 'text-white/30 hover:bg-white/5 hover:text-white/60'
            }`}
          title="Quick (IP)"
        >
          <span className="material-icons-round text-sm">flash_on</span>
          <span className="text-[6px] font-bold uppercase mt-0.5 tracking-wider">IP</span>
        </button>
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-xs font-bold text-primary uppercase tracking-wider mb-2">
        Coordinates Precision
      </label>
      <div className="grid grid-cols-3 gap-2">
        {modes.map((mode) => {
          const config = ACCURACY_MODES[mode]
          const isSelected = value === mode
          return (
            <button
              key={mode}
              type="button"
              onClick={() => handleChange(mode)}
              className={`p-3 rounded-lg border transition-all text-left relative overflow-hidden group ${isSelected
                ? 'bg-primary/20 border-primary text-white shadow-[0_0_15px_rgba(242,13,128,0.2)]'
                : 'bg-black/20 border-white/5 text-white/50 hover:bg-white/5 hover:border-white/20'
                }`}
            >
              {isSelected && <div className="absolute inset-0 bg-primary/10 animate-pulse"></div>}
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg ${isSelected ? 'text-primary neon-glow' : ''}`}>{config.icon}</span>
                  <span className="font-mono font-bold text-xs tracking-wide uppercase">{config.shortLabel}</span>
                </div>
                <div className="text-[10px] text-white/40 font-mono">{config.accuracy}</div>
              </div>
            </button>
          )
        })}
      </div>
      <p className="text-[10px] text-white/40 font-mono border-l-2 border-white/10 pl-2">
        {ACCURACY_MODES[value].description}
      </p>
    </div>
  )
}
